import React from 'react';
import { useHistory } from 'react-router-dom';
import { ChevronRight, Clock, FileSignature } from 'lucide-react';
import { Button } from '@/components/ui/button';
import routeConfig from '@/config/routeConfig';
import { CERTIFICATE_TYPE_LABELS } from '@/entities/certificate';
import CancelOnboardingDialog from './CancelOnboardingDialog';
import {
	canCancelFromDashboard,
	describeOnboardingProgress,
} from './onboarding-progress';
import { useActiveOnboarding } from './use-active-onboarding';

/** Condensed view of a signature request in flight, shown on the dashboard. */
const OnboardingProgressCard: React.FC = () => {
	const history = useHistory();
	const { status, refresh } = useActiveOnboarding();
	const progress = describeOnboardingProgress(status);

	if (!status || !progress) return null;

	const certType = status.process?.cert_type;
	const certLabel = certType
		? (CERTIFICATE_TYPE_LABELS[certType] ?? certType)
		: null;

	return (
		<div
			data-testid="onboarding-progress"
			style={{
				display: 'flex',
				flexDirection: 'column',
				gap: 12,
				width: '100%',
				maxWidth: 560,
				margin: '0 auto 24px',
				padding: 20,
				textAlign: 'left',
				background: 'white',
				border: '1px solid #e5e7eb',
				borderRadius: 12,
			}}
		>
			<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
				{progress.isWaiting ? (
					<Clock aria-hidden="true" size={20} />
				) : (
					<FileSignature aria-hidden="true" size={20} />
				)}
				<strong>Tienes una solicitud de firma en curso</strong>
			</div>

			<div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
				{certLabel && <span>{certLabel} · </span>}
				<span>Solicitud </span>
				<code style={{ fontFamily: 'monospace' }}>
					{status.onboarding_ulid}
				</code>
			</div>

			<div>
				<div style={{ fontWeight: 600 }}>{progress.stepLabel}</div>
				<p style={{ margin: 0 }}>{progress.description}</p>
			</div>

			<div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
				<Button
					variant={progress.isWaiting ? 'outline' : 'default'}
					onClick={() => history.push(routeConfig.requestSignature)}
				>
					{progress.actionLabel}
					<ChevronRight />
				</Button>
				{canCancelFromDashboard(status) && (
					<CancelOnboardingDialog
						onboardingUlid={status.onboarding_ulid}
						onSettled={refresh}
					/>
				)}
			</div>
		</div>
	);
};

export default OnboardingProgressCard;
